import React, { useState, useEffect, useRef, useCallback } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { useDispatch } from 'react-redux';
import { mapFormToDatabase } from '../../services/utils/catalogueModificationUtils';
import {
    Button,
    Steps,
    Form,
    App,
} from 'antd';
// Import step components
import PermissionsStep from './Steps/PermissionsStep';
import LicenseStep from './Steps/LicenseStep';
// Import confirmation modal
import ConfirmPublishDraftModal from './Modals/ConfirmPublishDraftModal';
// Services and utilities
import { fetchAvailableLicenses } from '../../services/api/cataloguesApi';
import { publishDraftUnified } from '../../services/api/unifiedCatalogueService';
import { updateUserPublishedCatalogues } from '../../services/api/catalogueBaseService';
import { updateDraftedCatalogue } from '../../services/api/draftedCatalogueService';
import { setAvailableLicenses } from '../../redux/licenses/actions';
import { isValidDomainFormat, isDomainAvailable } from '../../services/utils/validationUtils';
import catalogEvents, { CATALOG_EVENTS } from '../../utils/catalogEvents';
import { createDefaultFormStructure } from '../../services/utils/catalogueDefaults';
import { notifyNewCatalogPublished } from '../../services/emailService';
import { getSubscriptionIdByCatalogueId } from '../../services/utils/subscriptionUtils';

const { Step } = Steps;

/**
 * A stepper component for creating a new catalogue
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onCancel - Function to handle cancel
 * @param {Function} props.onFinish - Function to handle finish
 * @param {number} props.currentStep - Current step index
 * @param {Function} props.setCurrentStep - Function to set current step
 */
const PublishCatalogStepper = ({
    onCancel,
    onFinish,
    currentStep,
    setCurrentStep,
    onFormDataChange,
    initialFormData = null,
    isEditMode = false,
    stepperKey,
    draftId = null, // ID of the draft to publish
    onRefreshCatalogues = null // Function to refresh catalog lists
}) => {
    // --- HANDLER UNIFICATO ---
    const handleCreateCatalogue = () => {
        if (draftId) {
            // If we have a draftId, show confirmation modal for publishing
            setShowConfirmModal(true);
        } else {
            // Original behavior for creating new catalogues
            if (onFinish) onFinish(formData);
        }
    };

    // State for confirmation modal
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);

    // Get message instance from App context
    const { message: messageApi } = App.useApp();
    // Redux dispatch
    const dispatch = useDispatch();
    // Form state
    const [form] = Form.useForm();
    const defaultFormData = {
        ...createDefaultFormStructure(),
        // Campi aggiuntivi specifici per la pubblicazione
        licenseList: [],
        accessMethod: '',
        licenseRelationship: ''
    };
    const [formData, setFormData] = useState(initialFormData || defaultFormData);

    // Domain validation states
    const [domainValidating, setDomainValidating] = useState(false);
    const [formattedDomainUrl, setFormattedDomainUrl] = useState('');

    // Reset formData when initialFormData or isEditMode changes
    useEffect(() => {

        if (isEditMode && initialFormData) {
            const deepCopy = cloneDeep(initialFormData);
            setFormData(deepCopy);
            form.setFieldsValue(deepCopy);
        } else if (initialFormData) {
            // Anche se non è editMode, se abbiamo initialFormData (caso del draft), usalo
            const deepCopy = cloneDeep(initialFormData);
            setFormData(deepCopy);
            form.setFieldsValue(deepCopy);
        } else {
            setFormData(defaultFormData);
            form.resetFields();
        }
    }, [stepperKey, initialFormData, isEditMode]);

    // Notify parent of formData changes for draft logic
    useEffect(() => {
        if (typeof onFormDataChange === 'function') {
            onFormDataChange(formData);
        }
    }, [formData, onFormDataChange]);

    // Loading states
    const [loadingLicenses, setLoadingLicenses] = useState(false);

    // Available licenses
    const [availableLicenses, setAvailableLicensesState] = useState([]);

    // Validazione per i domini consentiti (accetta domini completi come example.com)
    const isValidAllowedDomain = (domain) => {
        if (!domain) return false;
        // Regex per domini validi (supporta sottodomini e TLD)
        const domainRegex = /^([a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
        return domainRegex.test(domain);
    };

    // License upgrade modal state
    const [upgradeModal, setUpgradeModal] = useState({ visible: false, license: null });

    // Function to load licenses
    const loadLicenses = useCallback(async () => {
        setLoadingLicenses(true);
        try {
            const licenses = await fetchAvailableLicenses();
            setAvailableLicensesState(licenses);
            // 🔄 AGGIORNA ANCHE IL REDUX STORE
            dispatch(setAvailableLicenses(licenses));
        } catch (error) {
            console.error('Error fetching licenses:', error);
            messageApi.error(error.message || 'Failed to load available licenses.');
            setAvailableLicensesState([]);
        } finally {
            setLoadingLicenses(false);
        }
    }, [dispatch, messageApi]);

    // Fetch available licenses on mount
    useEffect(() => {
        loadLicenses();
    }, [loadLicenses]);

    // Handle form field changes
    const handleFormChange = (changedValues, allValues) => {
        isFormUpdating.current = true; // Prevent the useEffect from updating the form
        setFormData(prevFormData => {
            let newFormData = { ...prevFormData };

            // Process all changes (merge nested objects to avoid losing flags)
            for (const key in changedValues) {
                if (changedValues.hasOwnProperty(key)) {
                    const value = changedValues[key];
                    if (Array.isArray(key)) {
                        let currentLevel = newFormData;
                        key.forEach((k, index) => {
                            if (index === key.length - 1) {
                                currentLevel[k] = value;
                            } else {
                                if (!currentLevel[k]) {
                                    currentLevel[k] = {};
                                }
                                currentLevel = currentLevel[k];
                            }
                        });
                    } else if (typeof value === 'object' && !Array.isArray(value) && newFormData[key] && typeof newFormData[key] === 'object') {
                        // Merge nested object changes (e.g., permissions)
                        newFormData[key] = { ...newFormData[key], ...value };
                    } else {
                        newFormData[key] = value;
                    }
                }
            }

            return newFormData;
        });
    };

    // Sync form values when formData changes - moved outside of setState to avoid render-time updates
    // Use a ref to track if the update comes from form itself to avoid loops
    const isFormUpdating = useRef(false);

    useEffect(() => {
        if (form && formData && !isFormUpdating.current) {
            isFormUpdating.current = true;
            form.setFieldsValue(formData);
            // Reset the flag after a short delay to allow form updates
            setTimeout(() => {
                isFormUpdating.current = false;
            }, 0);
        }
    }, [form, formData]);

    // Steps content - License first, then Permissions
    const steps = [
        {
            title: 'Select linked plan',
            content: (
                <LicenseStep
                    form={form}
                    formData={formData}
                    handleFormChange={handleFormChange}
                    availableLicenses={availableLicenses}
                    loadingLicenses={loadingLicenses}
                    setUpgradeModal={setUpgradeModal}
                    upgradeModal={upgradeModal}
                />
            ),
        },
        {
            title: 'Permissions',
            content: (
                <PermissionsStep
                    form={form}
                    formData={formData}
                    setFormData={setFormData}
                    handleFormChange={handleFormChange}
                    isValidAllowedDomain={isValidAllowedDomain}
                    isDomainInputDisabled={false}
                    isValidDomainFormat={isValidDomainFormat}
                    isDomainAvailable={isDomainAvailable}
                    setDomainValidating={setDomainValidating}
                    domainValidating={domainValidating}
                    setFormattedDomainUrl={setFormattedDomainUrl}
                    availableLicenses={availableLicenses}
                    selectedLicenseId={formData.licenseList}
                />
            )
        }
    ];

    // Function to handle next step
    const handleNext = () => {
        form.validateFields()
            .then(() => {
                setCurrentStep(currentStep + 1);
            })
            .catch(error => {
                console.error('Validation failed:', error);
                messageApi.error('Please fix the validation errors before proceeding');
            });
    };

    // Function to handle previous step
    const handlePrev = () => {
        setCurrentStep(currentStep - 1);
    };

    // Determine if the Next button should be disabled
    const isNextDisabled = () => {
        if (currentStep === 0) { // Select linked plan step (first step)
            return !formData.licenseList || formData.licenseList.length === 0;
        }
        // For Permissions step (second step), no specific validation needed here
        return false;
    };

    // Handle publish confirmation
    const handlePublishConfirm = async () => {
        if (!draftId) return;

        setIsPublishing(true);
        try {

            // Map form structure to database structure before saving
            let finalData = cloneDeep(formData);

            // ✅ USA IL MAPPING CENTRALIZZATO INSTEAD OF MANUAL MAPPING
            finalData = mapFormToDatabase(finalData);

            // === LOGICA SPECIFICA PER LA PUBBLICAZIONE ===

            // Set isPublic always to true for published catalogues
            finalData.isPublic = true;

            // Set allowedDomain to null for published catalogues
            finalData.allowedDomain = null;

            // Handle webDomain formatting
            if (finalData.webDomain && !finalData.webDomain.includes('.')) {
                finalData.webDomain = `www.${finalData.webDomain}.webvto.it`;
            }

            // Get webUrl and other data from selected license/subscription
            if (finalData.licenseList && availableLicenses.length > 0) {
                const selectedLicense = availableLicenses.find(license => license.id === finalData.licenseList);
                if (selectedLicense) {
                    if (selectedLicense.webUrl) finalData.webUrl = selectedLicense.webUrl;
                    if (selectedLicense.dataConsumption) finalData.dataConsumption = selectedLicense.dataConsumption;
                    if (selectedLicense.skin) finalData.skin = selectedLicense.skin;
                    if (selectedLicense.takePicTemplate) finalData.takePicTemplate = selectedLicense.takePicTemplate;
                    if (selectedLicense.hdrEnvironment) finalData.hdrEnvironment = selectedLicense.hdrEnvironment;
                    if (selectedLicense.list_service) finalData.list_service = selectedLicense.list_service;
                }
            }

            // Set fixed values for published catalogues
            finalData.appIdIos = "1586661818";
            finalData.appPackage = "arshades";
            finalData.appPackageIos = "ARShades-demo";
            finalData.codePath = "11";
            finalData.showSingleDotList = true;

            // Set default null values
            finalData.coverImage = finalData.coverImage || null;
            finalData.glassesNumber = finalData.glassesNumber || null;
            finalData.lightIntensityExponent = finalData.lightIntensityExponent || null;
            finalData.noBackgroundLogo = finalData.noBackgroundLogo || null;
            finalData.storageConsumption = finalData.storageConsumption || null;

            // Save current changes to the draft before publishing
            await updateDraftedCatalogue(draftId, finalData);

            // Publish the draft
            const publishedCatalogueId = await publishDraftUnified(draftId);

            // Add the published catalogue to user's list
            await updateUserPublishedCatalogues(publishedCatalogueId);

            // 🔄 RICARICA LE LICENZE DOPO LA PUBBLICAZIONE
            try {
                await loadLicenses();
            } catch (licenseError) {
                console.error('Failed to reload licenses:', licenseError);
            }

            // Refresh catalog lists
            if (onRefreshCatalogues) {
                await onRefreshCatalogues();
            }

            // Emetti evento per notificare che un catalogo è stato pubblicato
            catalogEvents.emit(CATALOG_EVENTS.CATALOG_PUBLISHED, {
                catalogId: publishedCatalogueId,
                draftId: draftId
            });

            // 📧 Notify new catalog publication via Firebase Function
            try {
                // Usa l'ID della subscription selezionata dal form, o cerca nella collezione come fallback
                let subscriptionId = finalData.licenseList;

                // Se non abbiamo la subscription dal form, cerchiamo nella collezione
                if (!subscriptionId) {
                    subscriptionId = await getSubscriptionIdByCatalogueId(publishedCatalogueId);
                }

                if (subscriptionId) {
                    await notifyNewCatalogPublished(publishedCatalogueId, subscriptionId);
                } else {
                    console.warn('⚠️ No subscription found for catalog, skipping publication notification');
                }
            } catch (notificationError) {
                console.error('❌ Failed to send publication notification:', notificationError);
                // Non bloccare la pubblicazione se la notifica fallisce
                messageApi.warning('Catalog published successfully, but notification email failed to send');
            }

            setShowConfirmModal(false);

            // Call onFinish to close the modal
            if (onFinish) onFinish();

        } catch (error) {
            console.error('Error publishing draft:', error);
            messageApi.error('Failed to publish draft. Please try again.');
        } finally {
            setIsPublishing(false);
        }
    };

    // Handle publish cancel
    const handlePublishCancel = async () => {
        if (!draftId) {
            setShowConfirmModal(false);
            return;
        }

        setIsPublishing(true);
        try {
            // Map form structure to database structure before saving
            let finalData = cloneDeep(formData);

            // Permissions fields mapping
            if (finalData.permissions) {
                if (finalData.permissions.public !== undefined) {
                    finalData.isPublic = finalData.permissions.public;
                }
                if (finalData.permissions.requireLogin !== undefined) {
                    finalData.isLoginRequired = finalData.permissions.requireLogin;
                }
                if (finalData.permissions.loginPassword !== undefined) {
                    finalData.loginPassword = finalData.permissions.loginPassword;
                }
                if (finalData.permissions.iframeOnlyAccess !== undefined) {
                    finalData.isIframeOnlyAcces = finalData.permissions.iframeOnlyAccess;
                }
                if (finalData.permissions.allowedDomain !== undefined) {
                    finalData.allowedDomain = finalData.permissions.allowedDomain;
                }
                // Remove the nested permissions object after mapping
                delete finalData.permissions;
            }

            // Save current changes to the draft
            await updateDraftedCatalogue(draftId, finalData);

            // Refresh catalog lists
            if (onRefreshCatalogues) {
                await onRefreshCatalogues();
            }

            messageApi.success('Draft changes saved successfully!');
            setShowConfirmModal(false);

            // Call onFinish to close the modal
            if (onFinish) onFinish();

        } catch (error) {
            console.error('Error saving draft changes:', error);
            messageApi.error('Failed to save draft changes. Please try again.');
        } finally {
            setIsPublishing(false);
        }
    };

    return (
        <div style={{ width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
            <div style={{ display: 'flex', gap: '1.5rem', height: '35rem', width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                {/* Sezione sinistra con steps */}
                <div style={{ width: '15.625rem', flexShrink: 0, borderRight: '1px solid #f0f0f0', paddingRight: '1rem' }}>
                    <Steps current={currentStep} direction="vertical" style={{ height: '100%' }}>
                        {steps.map(item => (
                            <Step key={item.title} title={item.title} />
                        ))}
                    </Steps>
                </div>

                {/* Contenuto principale */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', width: 'calc(100% - 17rem)', boxSizing: 'border-box', overflow: 'hidden' }}>
                    {/* Contenuto dello step corrente */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem', width: '100%', boxSizing: 'border-box' }}>
                        <div className="steps-content" style={{ width: '100%', boxSizing: 'border-box' }}>
                            {steps[currentStep].content}
                        </div>
                    </div>
                </div>
            </div>
            <div className="steps-action" style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between' }}>
                {currentStep > 0 && (
                    <Button onClick={handlePrev}>
                        Previous
                    </Button>
                )}

                {currentStep === 0 && (
                    <Button onClick={onCancel}>
                        Cancel
                    </Button>
                )}

                {currentStep < steps.length - 1 && (
                    <Button type="primary" onClick={handleNext} disabled={isNextDisabled()}>
                        Next
                    </Button>
                )}

                {currentStep === steps.length - 1 && (
                    <Button type="primary" onClick={handleCreateCatalogue}>
                        {draftId ? 'Publish Draft' : (isEditMode ? 'Update Catalogue' : 'Create Catalogue')}
                    </Button>
                )}
            </div>
            {draftId && (
                <ConfirmPublishDraftModal
                    visible={showConfirmModal}
                    onConfirm={handlePublishConfirm}
                    onCancel={handlePublishCancel}
                    isPublishing={isPublishing}
                />
            )}
        </div>
    );
};

export default PublishCatalogStepper;